import { useEffect, useMemo, useRef, useState } from "react";
import { useSocket } from "src/components/SocketContext";
import {
  DataPoint,
  fetchWrapper,
  HpbPoint,
  LtpMessage,
  MoMPoint,
  OhlcvDataPoint,
  RsiPoint,
  YoYPoint,
} from "src/helpers";

export function useStockCharts(
  symbol: string,
  token: string,
  includeLifetime = true,
  includeMoM = true,
  includeYoY = true,
  _includeOrderBlock = true,
  selectedDate?: string,
  isDashboard?: boolean,
  includeRsi = true,
  includeHpb = true,
  pause = false,
  includeOhlc = true,
  includeHistorical = false,
) {
  const [chartData, setChartData] = useState<DataPoint[]>([]);
  const [historicalDates, setHistoricalDates] = useState<string[]>([]);
  const [dataSymbol, setDataSymbol] = useState<string | null>(null);
  const [rsiData, setRsiData] = useState<RsiPoint[]>([]);
  const [ohlcvData, setOhlcvData] = useState<OhlcvDataPoint[]>([]);
  const [hpbData, setHpbData] = useState<HpbPoint[]>([]);
  const [hpbYear, setHpbYear] = useState<number | string>();
  const [yoyStockData, setYoyStockData] = useState<YoYPoint[]>([]);
  const [momStockData, setMoMStockData] = useState<MoMPoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [hoveredCandle, setHoveredCandle] = useState<DataPoint | null>(null);
  const [liveCandleUpdate, setLiveCandleUpdate] =
    useState<Partial<DataPoint> | null>(null);
  /** Inputs of the last successfully applied fetch, used to skip redundant refetches. */
  const loadedKeyRef = useRef<string | null>(null);

  const { emit, on, off, isConnected } = useSocket();

  const activeCandle = useMemo(() => {
    if (hoveredCandle) return hoveredCandle;
    const lastDataCandle =
      chartData.length > 0 ? chartData[chartData.length - 1] : null;
    if (liveCandleUpdate && lastDataCandle) {
      return {
        ...lastDataCandle,
        ...liveCandleUpdate,
      };
    }
    return lastDataCandle;
  }, [hoveredCandle, chartData, liveCandleUpdate]);

  useEffect(() => {
    if (!symbol || !isConnected) return;

    const handleStockUpdate = (messages: LtpMessage[]) => {
      const updated = messages.find(
        (m: LtpMessage) => m.symbol_name === symbol,
      );
      if (updated) {
        setLiveCandleUpdate({
          open: Number(updated.open),
          close: Number(updated.ltp),
          high: Number(updated.high),
          low: Number(updated.low),
          volume: Number(updated.volume),
          date: updated.ltp_date ? new Date(updated.ltp_date) : undefined,
        });
      }
    };

    on("stock_update", handleStockUpdate);
    emit("subscribe_stock", { symbol: [symbol] });

    return () => {
      off("stock_update", handleStockUpdate);
      emit("unsubscribe_stock", { symbol: [symbol] });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, isConnected]);

  // Reset live update when symbol changes
  useEffect(() => {
    setLiveCandleUpdate(null);
  }, [symbol]);

  useEffect(() => {
    if (!symbol || pause) return;

    // `pause` flips on/off while the dashboard reloads its table, and that must not
    // re-issue a request we already have the answer for: a second response replaces
    // every data array with a fresh reference and makes the charts redraw again.
    const fetchKey = [
      symbol,
      token,
      includeOhlc,
      includeRsi,
      includeLifetime,
      includeMoM,
      includeYoY,
      includeHpb,
      includeHistorical,
      isDashboard ? selectedDate : "",
    ].join("|");
    if (loadedKeyRef.current === fetchKey) return;

    const controller = new AbortController();
    const { signal } = controller;
    const headers = { Authorization: `Bearer ${token}` };

    const fetchData = async () => {
      try {
        setLoading(true);
        const requests = [
          includeOhlc
            ? fetchWrapper.get(
                `/api/dashboard/ohlcdata?name=${encodeURIComponent(symbol)}${isDashboard && selectedDate ? `&date=${selectedDate}` : ""}`,
                undefined,
                headers,
                signal,
              )
            : Promise.resolve({ data: [] }),
          includeRsi
            ? fetchWrapper.get(
                `/api/dashboard/rsi?symbol=${encodeURIComponent(symbol)}${isDashboard && selectedDate ? `&date=${selectedDate}` : ""}`,
                undefined,
                headers,
                signal,
              )
            : Promise.resolve({ data: [] }),
          includeLifetime
            ? fetchWrapper.get(
                `/api/dashboard/seasonal/lifetime?symbol=${encodeURIComponent(symbol)}`,
                undefined,
                headers,
                signal,
              )
            : Promise.resolve({ data: [] }),
          includeMoM
            ? fetchWrapper.get(
                `/api/dashboard/seasonal/mom_return_trend?symbol=${encodeURIComponent(symbol)}`,
                undefined,
                headers,
                signal,
              )
            : Promise.resolve({ data: [] }),
          includeYoY
            ? fetchWrapper.get(
                `/api/dashboard/seasonal/yoy_average?symbol=${encodeURIComponent(symbol)}`,
                undefined,
                headers,
                signal,
              )
            : Promise.resolve({ data: [] }),
          includeHpb
            ? fetchWrapper.get(
                `/api/dashboard/seasonal/movement?symbol=${encodeURIComponent(symbol)}`,
                undefined,
                headers,
                signal,
              )
            : Promise.resolve({ data: [] }),
          // Fetched alongside the candles so the "picked in past" pins land in the
          // same state update — arriving separately re-rendered the chart a second time.
          includeHistorical
            ? fetchWrapper.get(
                `/api/dashboard/historical?symbol=${encodeURIComponent(symbol)}`,
                undefined,
                headers,
                signal,
              )
            : Promise.resolve({ data: [] }),
        ];

        const [
          priceRes,
          rsiRes,
          lifetimeRes,
          momRes,
          yoyRes,
          hpbRes,
          historicalRes,
        ] = await Promise.all(requests);

        if (signal.aborted) return;

        setChartData(priceRes.data || []);
        setYoyStockData(yoyRes.data || []);
        setMoMStockData(momRes.data || []);
        setRsiData(rsiRes.data || []);
        setOhlcvData(lifetimeRes.data || []);
        setHpbData(hpbRes.data || []);
        setHpbYear(hpbRes.since_year || null);
        setHistoricalDates(
          Array.isArray(historicalRes.data)
            ? historicalRes.data.map((d: { date: string }) => d.date)
            : [],
        );
        setDataSymbol(symbol);
        loadedKeyRef.current = fetchKey;
      } catch (error: unknown) {
        if (signal.aborted) return;
        console.error("Error fetching chart data:", error);
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    };

    void fetchData();
    return () => {
      controller.abort();
    };
  }, [
    symbol,
    token,
    includeLifetime,
    includeMoM,
    includeYoY,
    includeRsi,
    includeHpb,
    includeOhlc,
    includeHistorical,
    selectedDate,
    isDashboard,
    pause,
  ]);

  return {
    chartData,
    historicalDates,
    rsiData,
    ohlcvData,
    yoyStockData,
    momStockData,
    hpbData,
    hpbYear,
    loading: loading || (!!symbol && dataSymbol !== symbol) || pause,
    hoveredCandle,
    setHoveredCandle,
    activeCandle,
  };
}
